import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageCrop } from './ImageCropBox';

interface CroppedImage {
  src: string;
  /** Box centre and size on the frame, as fractions of it. */
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  crop?: ImageCrop | null;
}

interface ImagePreviewCropProps {
  image: CroppedImage;
  /** Fired on release — one crop gesture is one undo step. */
  onChange: (crop: ImageCrop) => void;
  onDone: () => void;
}

const FULL: ImageCrop = { x: 0, y: 0, w: 1, h: 1 };
const MIN = 0.05;
const HANDLES = ['tl', 't', 'tr', 'l', 'r', 'bl', 'b', 'br'] as const;

/**
 * Cropping an image overlay on the video frame itself.
 *
 * The overlay's box never moves: it is the crop rect. So to give the author something to drag,
 * the whole source is ghosted back in behind the box — scaled and offset so the *current* crop
 * lands exactly on the box — and the rect is dragged across that ghost. On release the crop is
 * committed, the ghost re-derives from it, and the rect is back over the box.
 *
 * `base` is the crop the ghost is pinned to. It is frozen for the length of a gesture, so only
 * the rect moves while the mouse is down; `local` is the live rect.
 */
const ImagePreviewCrop: React.FC<ImagePreviewCropProps> = ({ image, onChange, onDone }) => {
  const crop = image.crop || FULL;
  const ref = useRef<HTMLDivElement>(null);
  const [local, setLocal] = useState<ImageCrop>(crop);
  const [base, setBase] = useState<ImageCrop>(crop);
  const [dragging, setDragging] = useState<string | null>(null);

  useEffect(() => {
    setLocal(crop);
    setBase(crop);
  }, [crop.x, crop.y, crop.w, crop.h, image.src]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDone(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDone]);

  const drag = useCallback((e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    const frame = ref.current?.parentElement;
    if (!frame) return;
    // The ghost's true size, derived from the unrotated frame rather than measured: this
    // container is rotated, and a rotated element's bounding box is not its own size.
    const frameRect = frame.getBoundingClientRect();
    const start = { ...local };
    const ghostW = frameRect.width * (image.width / Math.max(0.01, start.w));
    const ghostH = frameRect.height * (image.height / Math.max(0.01, start.h));
    const rad = (-image.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const startX = e.clientX;
    const startY = e.clientY;

    setBase(start);
    setDragging(handle);
    let latest = start;

    const onMouseMove = (ev: MouseEvent) => {
      // Pointer delta back into the image's own axes, so a rotated overlay crops along the
      // edges the author can see rather than along the frame's.
      const px = ev.clientX - startX;
      const py = ev.clientY - startY;
      const dx = (px * cos - py * sin) / ghostW;
      const dy = (px * sin + py * cos) / ghostH;
      const c = { ...start };

      if (handle === 'move') {
        c.x = Math.max(0, Math.min(1 - start.w, start.x + dx));
        c.y = Math.max(0, Math.min(1 - start.h, start.y + dy));
      } else {
        if (handle.includes('l')) {
          const nx = Math.max(0, Math.min(start.x + start.w - MIN, start.x + dx));
          c.w = start.x + start.w - nx;
          c.x = nx;
        }
        if (handle.includes('r')) c.w = Math.max(MIN, Math.min(1 - start.x, start.w + dx));
        if (handle.includes('t')) {
          const ny = Math.max(0, Math.min(start.y + start.h - MIN, start.y + dy));
          c.h = start.y + start.h - ny;
          c.y = ny;
        }
        if (handle.includes('b')) c.h = Math.max(MIN, Math.min(1 - start.y, start.h + dy));
      }
      latest = c;
      setLocal(c);
    };
    const onMouseUp = () => {
      setDragging(null);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      onChange(latest);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [local, image.width, image.height, image.rotation, onChange]);

  // The ghost, in fractions of the box: the sub-rect `base` of it covers the box exactly.
  const ghost: React.CSSProperties = {
    left: `${(-base.x / base.w) * 100}%`,
    top: `${(-base.y / base.h) * 100}%`,
    width: `${(1 / base.w) * 100}%`,
    height: `${(1 / base.h) * 100}%`,
  };

  const cropped = local.x > 0 || local.y > 0 || local.w < 1 || local.h < 1;

  return (
    <>
    <div
      ref={ref}
      className="preview-crop"
      style={{
        left: `${image.x * 100}%`,
        top: `${image.y * 100}%`,
        width: `${image.width * 100}%`,
        height: `${image.height * 100}%`,
        transform: `translate(-50%, -50%) rotate(${image.rotation}deg)`,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="preview-crop-ghost" style={ghost}>
        <img src={image.src} alt="crop source" draggable={false} />

        <div className="preview-crop-mask" style={{ left: 0, top: 0, width: '100%', height: `${local.y * 100}%` }} />
        <div className="preview-crop-mask" style={{ left: 0, top: `${(local.y + local.h) * 100}%`, width: '100%', bottom: 0 }} />
        <div className="preview-crop-mask" style={{ left: 0, top: `${local.y * 100}%`, width: `${local.x * 100}%`, height: `${local.h * 100}%` }} />
        <div className="preview-crop-mask" style={{ left: `${(local.x + local.w) * 100}%`, top: `${local.y * 100}%`, right: 0, height: `${local.h * 100}%` }} />

        <div
          className="preview-crop-rect"
          style={{
            left: `${local.x * 100}%`, top: `${local.y * 100}%`,
            width: `${local.w * 100}%`, height: `${local.h * 100}%`,
          }}
          onMouseDown={(e) => drag(e, 'move')}
        >
          <div className="preview-crop-grid">
            <div className="preview-crop-grid-h1" />
            <div className="preview-crop-grid-h2" />
            <div className="preview-crop-grid-v1" />
            <div className="preview-crop-grid-v2" />
          </div>
          {HANDLES.map(h => (
            <div
              key={h}
              className={`preview-crop-handle preview-crop-handle-${h} ${dragging === h ? 'active' : ''}`}
              onMouseDown={(e) => drag(e, h)}
            />
          ))}
        </div>
      </div>
    </div>

    {/* A sibling of the box, not a child: the box is rotated and clipped by the frame, and the
        toolbar has to stay upright and reachable however small or turned the overlay is. */}
    <div
      className="preview-crop-toolbar"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <span>{cropped ? `${Math.round(local.w * 100)}% × ${Math.round(local.h * 100)}%` : 'Full image'}</span>
      <button className="btn-sm" disabled={!cropped} onClick={() => { setLocal(FULL); setBase(FULL); onChange(FULL); }}>
        Reset
      </button>
      <button className="btn-sm btn-accent" onClick={onDone}>Done</button>
    </div>
    </>
  );
};

export default ImagePreviewCrop;
